// Photo bytes for the weekly report's Word export (#576).
//
// ⚠️ IN THE BROWSER, because nothing else can read them. Site photos live in
// the job's SharePoint folder, and the API has no Graph permission to read
// files; only the signed-in user's delegated token can. So the export fetches
// each photo here and embeds it, rather than the API building the document.
//
// Each photo is fetched through its driveItem's pre-authenticated
// `@microsoft.graph.downloadUrl`, the same route `fetchLatestScheduleXml`
// already uses to pull file bytes into the page, then shrunk for the document:
// a week of daily logs can carry dozens of photos, and at upload size (up to
// 1920 px) the file would be too big to email.

import { resolveDownloadUrl } from '@/lib/graphUpload';
import { compressImageSized } from '@/lib/compressImage';

export interface EmbeddedPhoto {
  data: Uint8Array;
  type: 'jpg' | 'png';
  width: number;
  height: number;
}

/** Longest side in the document, in pixels. Enough for a half-page photo to print sharply. */
export const EMBED_MAX_DIM = 1280;
const EMBED_QUALITY = 0.78;
/** Same politeness as the thumbnail pool: one worker per request, a few at a time. */
const CONCURRENCY = 4;
/**
 * How long one photo may take, lookup and download together. A stalled request
 * would otherwise hold its worker forever and the export would never finish;
 * past this the photo is left out and named, like any other that won't load.
 */
export const PHOTO_TIMEOUT_MS = 45_000;

/** Resolve with the promise's value, or null once `ms` passes without one. */
export function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(null), ms);
    p.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

async function loadOne(webUrl: string, getToken: () => Promise<string>): Promise<EmbeddedPhoto | null> {
  const url = await resolveDownloadUrl(webUrl, getToken);
  if (!url) return null;
  // Pre-authenticated and short-lived: fetched WITHOUT an Authorization header.
  const res = await fetch(url);
  if (!res.ok) return null;
  const blob = await res.blob();
  const name = webUrl.split('/').pop() || 'photo';
  // ⚠️ The declared type, not the bytes, decides whether compressImageSized
  // decodes at all: it passes anything not image/* through untouched. Storage
  // can answer application/octet-stream for a perfectly good JPEG, so a
  // non-image type is read as an image and the decoder decides.
  const type = blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
  const shrunk = await compressImageSized(new File([blob], name, { type }), EMBED_MAX_DIM, EMBED_QUALITY);
  // A file the browser couldn't decode (a HEIC on some browsers) passes
  // through unchanged, and Word can't show it either: leave it out, named.
  if (!shrunk.reencoded || !shrunk.width || !shrunk.height) return null;
  return {
    data: new Uint8Array(await shrunk.file.arrayBuffer()),
    type: 'jpg',
    width: shrunk.width,
    height: shrunk.height,
  };
}

/**
 * Fetch and shrink every photo. Never throws: a photo that can't be loaded is
 * left out of the map, and the document names it in its place, so a missing
 * photo is visible to the PM reviewing the file rather than silently gone.
 */
export async function loadReportPhotos(
  webUrls: string[],
  getToken: () => Promise<string>,
  onProgress?: (done: number, total: number) => void,
): Promise<Map<string, EmbeddedPhoto>> {
  const out = new Map<string, EmbeddedPhoto>();
  const queue = [...new Set(webUrls)];
  const total = queue.length;
  let done = 0;
  onProgress?.(0, total);
  const worker = async () => {
    for (let next = queue.shift(); next !== undefined; next = queue.shift()) {
      try {
        const p = await withTimeout(loadOne(next, getToken), PHOTO_TIMEOUT_MS);
        if (p) out.set(next, p);
      } catch {
        /* left out; the document marks it */
      }
      onProgress?.(++done, total);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
  return out;
}

/** The E&W logo for the document header, from the app's own public folder. Null if it can't be read. */
export async function loadLogo(): Promise<EmbeddedPhoto | null> {
  try {
    const res = await fetch('/ew-logo-horizontal-green.png');
    if (!res.ok) return null;
    const data = new Uint8Array(await res.arrayBuffer());
    const size = pngSize(data);
    // The header scales it by the file's own aspect, so a new logo isn't squashed.
    return size ? { data, type: 'png', ...size } : null;
  } catch {
    return null;
  }
}

/** Width and height from a PNG's IHDR chunk, or null when the bytes aren't a PNG. */
export function pngSize(data: Uint8Array): { width: number; height: number } | null {
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (data.length < 24 || sig.some((b, i) => data[i] !== b)) return null;
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const width = view.getUint32(16);
  const height = view.getUint32(20);
  return width && height ? { width, height } : null;
}
